import {SheetProps} from "@mui/joy";
import {DateArg, differenceInMilliseconds, isAfter} from "date-fns";
import {isEqual} from "lodash";
import {useEffect, useRef, useState} from "react";
import {usePrevious} from "@uidotdev/usehooks";

export interface JoyDecos {
    color?: SheetProps["color"];
    variant?: SheetProps["variant"];
}

export interface DecayScene<FrameT> {
    afterMillis?: number;
    frame: FrameT;
}

export const DECO_DELAY_SCENES: DecayScene<JoyDecos>[] = [
    {frame: {variant: "solid"}},
    {afterMillis: 30 * 1000, frame: {variant: "soft"}},
    {afterMillis: 5 * 60 * 1000, frame: {variant: "plain"}},
];

type DecayTimer = number | NodeJS.Timeout | undefined;

// export type DecoDecayRef = { current: number | NodeJS.Timeout | undefined };

export interface UseDecayProps<FrameT> {
    scenes: DecayScene<FrameT>[];
    lastUpdated?: DateArg<Date>;
}

export function useDecoDecay({scenes, lastUpdated}: UseDecayProps<JoyDecos>): JoyDecos {
    const timerRef = useRef<DecayTimer>(undefined);
    const [rv, setRv] = useState<JoyDecos>((scenes[scenes.length - 1])?.frame ?? {});
    const prevLastUpdated = usePrevious(lastUpdated);

    useEffect(() => {
        if (!!lastUpdated && (!prevLastUpdated || isAfter(lastUpdated, prevLastUpdated)) &&
            !!timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }

        if (!timerRef.current && !!lastUpdated) {
            let sp = 0;
            let sclk = 0;
            const sinceLastUpdateMillis = differenceInMilliseconds(new Date(), lastUpdated);
            while ((sp + 1) < scenes.length) {
                const nextSceneLength = scenes[sp + 1]?.afterMillis;
                if (!nextSceneLength) {
                    sp = sp + 1;
                    continue;
                }
                const millisToGo = sinceLastUpdateMillis - sclk;
                if (millisToGo < nextSceneLength) {
                    // sp is pointing at the frame we want to return now
                    // // you must collapse it to a const here or risk aliasing
                    // // in the timeout function
                    const thisFrame = scenes[sp]?.frame ?? {};
                    setRv((prev) => {
                        if (isEqual(prev, thisFrame)) {
                            return prev;
                        } else {
                            return thisFrame;
                        }
                    });
                    const nextFrame = scenes[sp + 1]?.frame ?? {};
                    timerRef.current = setTimeout(() => {
                        setRv((prev) => {
                            if (isEqual(prev, nextFrame)) {
                                return prev;
                            } else {
                                return nextFrame;
                            }
                        });
                        timerRef.current = undefined;
                    }, nextSceneLength - millisToGo);
                    break;
                }
                sp = sp + 1;
                sclk = sclk + nextSceneLength;
            }
            if (!isEqual(rv, scenes[sp]?.frame)) {
                setRv(scenes[sp]?.frame ?? {});
            }
        }

        return () => {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, [lastUpdated, prevLastUpdated, rv, scenes, timerRef]);

    return rv ;
}


